import React, { useState, useEffect, useContext, useMemo } from "react";
import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaTimes,
  FaDownload,
  FaTruckLoading,
  FaEye,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";

const API_BASE = "https://nissi-india.com/api";

const STNOutwardReport = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // Filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [stnOutwardNo, setStnOutwardNo] = useState("");
  const [status, setStatus] = useState("");
  const [outwardType, setOutwardType] = useState("");
  const [requestedStore, setRequestedStore] = useState("");

  // Dropdown / Data
  const [storeList, setStoreList] = useState([]);
  const [results, setResults] = useState([]);

  // localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "8";
  const branchType = (localStorage.getItem("branchType") || "HO").toUpperCase();
  const userId = localStorage.getItem("userId") || "";
  const loginUserId = localStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
      "x-loginuserid": loginUserId,
    }),
    [finYearId, finYear, branchId, branchType, userId, loginUserId]
  );

  const formatDateForApi = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return Number.isNaN(date.getTime()) ? dateStr : date.toLocaleDateString("en-GB");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount) || 0);
  };

  const clearFilters = () => {
    setFromDate("");
    setToDate("");
    setStnOutwardNo("");
    setStatus("");
    setOutwardType("");
    setRequestedStore("");
    setResults([]);
  };

  const getStatusColor = (statusText) => {
    switch ((statusText || "").trim().toLowerCase()) {
      case "closed":
        return isDark
          ? "bg-green-900 text-green-100"
          : "bg-green-100 text-green-800";

      case "not closed":
        return isDark
          ? "bg-yellow-900 text-yellow-100"
          : "bg-yellow-100 text-yellow-800";

      case "canceled":
      case "cancelled":
        return isDark
          ? "bg-red-900 text-red-100"
          : "bg-red-100 text-red-800";

      default:
        return isDark
          ? "bg-gray-700 text-gray-100"
          : "bg-gray-100 text-gray-800";
    }
  };

  const getTypeColor = (typeText) => {
    const type = (typeText || "").trim().toLowerCase();

    if (type.includes("direct")) {
      return isDark
        ? "bg-blue-900 text-blue-100"
        : "bg-blue-100 text-blue-800";
    }

    if (type.includes("reject")) {
      return isDark
        ? "bg-red-900 text-red-100"
        : "bg-red-100 text-red-800";
    }

    if (type.includes("outward")) {
      return isDark
        ? "bg-purple-900 text-purple-100"
        : "bg-purple-100 text-purple-800";
    }

    return isDark
      ? "bg-gray-700 text-gray-100"
      : "bg-gray-100 text-gray-800";
  };

  const fetchStores = async () => {
    try {
      const res = await axios.get(`${API_BASE}/fillStoreDropdown_rpt`, {
        params: { session_branchid: branchId },
        headers: commonHeaders,
      });

      const stores = Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data)
        ? res.data
        : [];

      setStoreList(stores);
    } catch (error) {
      console.error("Store dropdown fetch failed", error);
      toast.error("Failed to load store dropdown");
    }
  };

  useEffect(() => {
    fetchStores();
  }, [branchId, commonHeaders]);

  const handleSearch = async () => {
    if (!fromDate || !toDate) {
      toast.warning("Please select both From Date and To Date");
      return;
    }

    try {
      setLoading(true);

      const params = {
        branchID: branchId || "8",
        stnreqno: stnOutwardNo || "",
        search_fromdate: formatDateForApi(fromDate),
        search_todate: formatDateForApi(toDate),
        requestedstore: requestedStore || "Select",
        status: status || "Select",
        type: outwardType || "Select",
      };

      const res = await axios.get(`${API_BASE}/stnOutward_rpt`, {
        params,
        headers: commonHeaders,
      });

      const list = Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data)
        ? res.data
        : [];

      setResults(list);
      toast.success(`Loaded ${list.length} record(s)`);
    } catch (error) {
      console.error("STN Outward fetch failed", error);
      toast.error("Failed to fetch STN Outward list");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!results.length) {
      toast.warn("No data available to export");
      return;
    }

    const headers = [
      "Outward No",
      "Date",
      "To Branch",
      "Amount",
      "Status",
      "Type",
    ];

    const rows = results.map((item) => [
      item.OutwardNo || "",
      item.OutwardDate || "",
      item.ToB || "",
      item.AmountNew || 0,
      item.Status || "Not Closed",
      item.STNType || "",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row.map((col) => `"${String(col).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "stn_outward_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrintReport = () => {
    const params = {
      branchID: branchId || "8",
      stnreqno: stnOutwardNo || "",
      search_fromdate: fromDate ? formatDateForApi(fromDate) : "",
      search_todate: toDate ? formatDateForApi(toDate) : "",
      requestedstore: requestedStore || "Select",
      status: status || "Select",
      type: outwardType || "Select",
    };

    const query = new URLSearchParams(params).toString();
    window.open(`/inventory/STNOutwardReport_AllPrint?${query}`, "_blank");
  };

  const handleViewRow = (row) => {
    sessionStorage.setItem("printData", JSON.stringify(row));
    window.open(`/inventory/STNOutwardReport_Print?id=${row.fld_STN_Outwardid}`, "_blank");
  };

  return (
    <LayoutWrapper>
      <HeaderBar title="STN OUTWARD REPORT" />
      <ToastContainer />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-4 flex-wrap">
            <h1
              className={`text-2xl font-bold ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              STN Outward Report
            </h1>

            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark
                  ? "bg-blue-900 text-blue-100"
                  : "bg-blue-100 text-blue-800"
              }`}
            >
              {results.length} Records
            </span>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-700" +
                    (showFilters ? " bg-gray-700" : "")
                  : "border-gray-300 text-gray-700 hover:bg-gray-100" +
                    (showFilters ? " bg-gray-100" : "")
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            <button
              onClick={handleExport}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700"
              }`}
            >
              <FaDownload className="w-4 h-4" />
              Export
            </button>

            <button
              onClick={handlePrintReport}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <FaPrint className="w-4 h-4" />
              Print Report
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div
            className={`rounded-xl p-6 mb-6 border ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
              <h3
                className={`text-lg font-semibold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Search Filters
              </h3>

              <button
                onClick={clearFilters}
                className={`text-sm flex items-center gap-2 ${
                  isDark
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                <FaTimes className="w-3 h-3" />
                Clear all
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  From Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  To Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  STN Outward No
                </label>
                <input
                  type="text"
                  value={stnOutwardNo}
                  onChange={(e) => setStnOutwardNo(e.target.value)}
                  placeholder="Enter STN outward number"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">All Status</option>
                  <option value="Closed">Closed</option>
                  <option value="Not Closed">Not Closed</option>
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Type
                </label>
                <select
                  value={outwardType}
                  onChange={(e) => setOutwardType(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">All Types</option>
                  <option value="From Outward">From Outward</option>
                  <option value="From Direct Outward">From Direct Outward</option>
                  <option value="From IN/Out (Rejected QTY)">
                    From IN/Out (Rejected QTY)
                  </option>
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Inward Store
                </label>
                <select
                  value={requestedStore}
                  onChange={(e) => setRequestedStore(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">All Stores</option>
                  {storeList.map((store, index) => (
                    <option
                      key={store.fld_storeid || store.value || index}
                      value={store.fld_storeid || store.value}
                    >
                      {store.fld_storename || store.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div
              className={`flex justify-between items-center pt-4 border-t ${
                isDark ? "border-gray-600" : "border-gray-200"
              } flex-wrap gap-3`}
            >
              <button
                onClick={clearFilters}
                className={`px-4 py-2 font-medium ${
                  isDark
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Clear Filters
              </button>

              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={handleSearch}
                  className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors`}
                >
                  <FaSearch className="w-4 h-4" />
                  Search
                </button>

                <button
                  onClick={handlePrintReport}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  <FaPrint className="w-4 h-4" />
                  Print
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : results.length > 0 ? (
          <div
            className={`rounded-xl border overflow-hidden ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            <div className="p-4 border-b border-gray-200 flex justify-between items-center flex-wrap gap-2">
              <h3
                className={`text-lg font-semibold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                STN Outward Results ({results.length})
              </h3>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={`bg-${baseColor}-${baseShade}`}>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Sl No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Outward No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      To Branch
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody
                  className={`divide-y divide-gray-200 ${
                    isDark ? "bg-gray-800" : "bg-white"
                  }`}
                >
                  {results.map((row, idx) => (
                    <tr
                      key={idx}
                      className={`transition-colors ${
                        isDark ? "hover:bg-gray-700" : "hover:bg-gray-50"
                      }`}
                    >
                      <td
                        className={`px-4 py-4 whitespace-nowrap text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {idx + 1}
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                        {row.OutwardNo || "-"}
                      </td>

                      <td
                        className={`px-4 py-4 whitespace-nowrap text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {row.OutwardDate || formatDate(row.fld_date)}
                      </td>

                      <td
                        className={`px-4 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {row.ToB || "-"}
                      </td>

                      <td
                        className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {formatCurrency(row.AmountNew)}
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                            row.Status
                          )}`}
                        >
                          {row.Status || "Not Closed"}
                        </span>
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(
                            row.STNType
                          )}`}
                        >
                          {row.STNType || "-"}
                        </span>
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleViewRow(row)}
                            className={`p-2 rounded transition-colors ${
                              isDark
                                ? "text-blue-400 hover:text-blue-300 hover:bg-gray-700"
                                : "text-blue-600 hover:text-blue-800 hover:bg-gray-100"
                            }`}
                            title="View"
                          >
                            <FaEye className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => handleViewRow(row)}
                            className={`p-2 rounded transition-colors ${
                              isDark
                                ? "text-green-400 hover:text-green-300 hover:bg-gray-700"
                                : "text-green-600 hover:text-green-800 hover:bg-gray-100"
                            }`}
                            title="Print"
                          >
                            <FaPrint className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          showFilters && (
            <div
              className={`text-center py-12 ${
                isDark ? "text-gray-400" : "text-gray-500"
              }`}
            >
              <FaTruckLoading className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p>No STN outward records found. Please use the filters to search.</p>
            </div>
          )
        )}
      </div>
    </LayoutWrapper>
  );
};

export default STNOutwardReport;